import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaSearch, FaMapMarkerAlt, FaStar, FaPhone, FaClock, FaStore, FaFilter } from 'react-icons/fa';
import ShopDetailPage from './ShopDetailPage';
import { subscribeToShops, Shop } from '../../firebase/firestore';

const Container = styled.div`
  padding: ${theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.md};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const SearchButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const FilterButton = styled.button`
  background: ${theme.colors.white};
  color: ${theme.colors.secondary};
  border: 2px solid ${theme.colors.lightGray};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const ShopsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ShopCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const ShopImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, ${theme.colors.lightGray} 0%, ${theme.colors.mediumGray} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.mediumGray};
  font-size: 3rem;
  position: relative;
`;

const ShopBadge = styled.div<{ type: 'premium' | 'verified' | 'new' }>`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: ${props => {
    switch (props.type) {
      case 'premium': return theme.colors.primary;
      case 'verified': return theme.colors.success;
      case 'new': return theme.colors.warning;
      default: return theme.colors.mediumGray;
    }
  }};
  color: ${theme.colors.white};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: bold;
  font-size: 0.8rem;
`;

const ShopContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const ShopTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.sm};
`;

const ShopLocation = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.mediumGray};
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.md};
`;

const ShopRating = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
  color: ${theme.colors.warning};
`;

const RatingText = styled.span`
  color: ${theme.colors.mediumGray};
  font-size: 0.9rem;
`;

const ShopServices = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const ServiceTag = styled.span`
  display: inline-block;
  background: ${theme.colors.lightGray};
  color: ${theme.colors.secondary};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.8rem;
  margin-right: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;

const ShopHours = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.mediumGray};
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.md};
`;

const ShopActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
    border: none;
    
    &:hover {
      background: ${theme.colors.secondary};
      transform: translateY(-2px);
    }
  ` : `
    background: ${theme.colors.white};
    color: ${theme.colors.secondary};
    border: 2px solid ${theme.colors.lightGray};
    
    &:hover {
      border-color: ${theme.colors.primary};
      color: ${theme.colors.primary};
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.mediumGray};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.lightGray};
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.secondary};
`;

const EmptyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const MarketplacePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    
    // Set up real-time listener for shops
    const unsubscribe = subscribeToShops((shopsData) => {
      setShops(shopsData);
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, []);

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shop.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shop.services.some(service => 
      service.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleShopClick = (shopId: string) => {
    setSelectedShopId(shopId);
  };

  const handleCallShop = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar 
        key={index} 
        style={{ 
          color: index < Math.floor(rating) ? theme.colors.warning : theme.colors.lightGray,
          fontSize: '0.9rem'
        }} 
      />
    ));
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading shops...</div>
        </div>
      </Container>
    );
  }

  if (selectedShopId) {
    const shop = shops.find(s => s.id === selectedShopId);
    if (!shop) return null;
    return <ShopDetailPage shop={shop} onBack={() => setSelectedShopId(null)} />;
  }

  return (
    <Container>
      <Header>
        <Title>Marketplace</Title>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search for shops or services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchButton>
            <FaSearch />
            Search
          </SearchButton>
          <FilterButton>
            <FaFilter />
            Filter
          </FilterButton>
        </SearchContainer>
      </Header>

      {filteredShops.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FaStore />
          </EmptyIcon>
          <EmptyTitle>No shops found</EmptyTitle>
          <EmptyDescription>
            Try adjusting your search terms or filters to find the perfect service provider.
          </EmptyDescription>
        </EmptyState>
      ) : (
        <ShopsGrid>
          {filteredShops.map((shop) => (
            <ShopCard key={shop.id} onClick={() => handleShopClick(shop.id)}>
              <ShopImage>
                <FaStore />
                <ShopBadge type={shop.badge}>
                  {shop.badge === 'premium' ? 'Premium' : 
                   shop.badge === 'verified' ? 'Verified' : 'New'}
                </ShopBadge>
              </ShopImage>
              <ShopContent>
                <ShopTitle>{shop.name}</ShopTitle>
                <ShopLocation>
                  <FaMapMarkerAlt />
                  {shop.location}
                </ShopLocation>
                <ShopRating>
                  <Stars>
                    {renderStars(shop.rating)}
                  </Stars>
                  <RatingText>
                    {shop.rating} ({shop.reviewCount} reviews)
                  </RatingText>
                </ShopRating>
                <ShopServices>
                  {shop.services.map((service, index) => (
                    <ServiceTag key={index}>{service}</ServiceTag>
                  ))}
                </ShopServices>
                <ShopHours>
                  <FaClock />
                  {Object.entries(shop.hours).map(([day, hours]) => 
                    hours.closed ? `${day}: Closed` : `${day}: ${hours.open} - ${hours.close}`
                  ).join(', ')}
                </ShopHours>
                <ShopActions>
                  <ActionButton variant="secondary" onClick={() => handleShopClick(shop.id)}>
                    View Details
                  </ActionButton>
                  <ActionButton 
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCallShop(shop.phone);
                    }}
                  >
                    <FaPhone />
                    Call
                  </ActionButton>
                </ShopActions>
              </ShopContent>
            </ShopCard>
          ))}
        </ShopsGrid>
      )}
    </Container>
  );
};

export default MarketplacePage;

